<template>
  <div class="relative">
    <div
      v-show="isBusy"
      class="absolute inset-0 bg-faint-white flex items-center justify-center z-20"
    >
      <Spinner :color="'text-black'" :size="10" />
    </div>

    <div class="w-full bg-white p-5 rounded shadow-md">
      <div class="w-full mb-4">
        <label class="uppercase font-semibold text-sm">
          Recipient Email <span class="text-red-500">*</span>
        </label>
        <Dropdown
          v-model="email"
          :options="recipients"
          optionLabel="display"
          optionValue="email"
          :editable="true"
          placeholder="Choose an address or type one"
          class="w-full"
          data-testid="recipient-email"
        />
        <p v-if="emailError" class="text-red-600 text-sm mt-1">{{ emailError }}</p>
      </div>

      <p class="text-gray-600 text-sm">
        The customer will receive a secure link that expires after 10 days. Sending a
        new link revokes any link already issued for this ticket.
      </p>

      <div class="flex justify-end mt-5">
        <AppButton color="green" label="Send Return Form" :icon="true" @click="send">
          <template v-slot:icon>
            <Icon iconType="mail" class="mr-2" />
          </template>
        </AppButton>
      </div>
    </div>

    <div class="w-full bg-white p-5 rounded shadow-md mt-6" v-if="links.length > 0">
      <div class="flex items-center mb-5">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
          Link History
        </h3>
        <hr class="flex-1 border-primary mx-4" />
      </div>

      <DataTable :value="links" class="p-datatable-sm return-form-links">
        <Column field="status" header="Status">
          <template #body="slotProps">
            <span
              class="px-2 rounded-lg"
              :class="{
                'bg-green-200 text-green-700': slotProps.data.status === 'pending',
                'bg-blue-200 text-blue-700': slotProps.data.status === 'submitted',
                'bg-gray-300 text-gray-700': slotProps.data.status === 'expired',
                'bg-red-200 text-red-700': slotProps.data.status === 'revoked',
              }"
              >{{ slotProps.data.status }}</span
            >
          </template>
        </Column>
        <Column field="sent_to" header="Sent To" />
        <Column field="sent_at" header="Sent">
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.sent_at) }}
          </template>
        </Column>
        <Column field="expires_at" header="Expires">
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.expires_at) }}
          </template>
        </Column>
        <Column header="">
          <template #body="slotProps">
            <AppButton
              v-if="slotProps.data.status === 'pending'"
              color="red"
              label="Revoke"
              @click="revoke(slotProps.data)"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <div class="w-full bg-white p-5 rounded shadow-md mt-6">
      <div class="flex items-center mb-5">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
          Email History
        </h3>
        <hr class="flex-1 border-primary mx-4" />
      </div>
      <EmailQueueTable ref="emailHistory" :referenceId="ticket.ticket_id" />
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner.vue";
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import ReturnFormLinkService from "@/services/returnFormLinkService";
import EmailQueueTable from "@/views/HR/Sales/Components/EmailQueueTable.vue";

export default {
  name: "SendReturnForm",
  mixins: [notificationMixin],
  components: {
    Spinner,
    AppButton,
    Icon,
    EmailQueueTable,
  },
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isBusy: false,
      email: "",
      emailError: "",
      links: [],
      recipients: [],
    };
  },
  async mounted() {
    await Promise.all([this.loadLinks(), this.loadRecipients()]);
  },
  methods: {
    async loadRecipients() {
      try {
        const result = await ReturnFormLinkService.getReturnFormRecipients(this.ticket.ticket_id);
        this.recipients = (result || []).map((r) => ({
          email: r.email,
          display: r.name ? `${r.name} <${r.email}> - ${r.source}` : `${r.email} - ${r.source}`,
        }));
      } catch (error) {
        this.recipients = [];
      }
      if (!this.email && this.recipients.length > 0) this.email = this.recipients[0].email;
    },
    async loadLinks() {
      try {
        const result = await ReturnFormLinkService.getReturnFormLinks(
          this.ticket.ticket_id
        );
        this.links = result.data || [];
      } catch (error) {
        this.links = [];
      }
    },
    validate() {
      const value = String(this.email || "").trim();

      if (!value) {
        this.emailError = "Please enter the recipient email address.";
        return false;
      }

      if (!this.$validator.isEmail(value)) {
        this.emailError = "Please enter a valid email address.";
        return false;
      }

      this.emailError = "";
      return true;
    },
    async send() {
      if (!this.validate()) {
        return;
      }

      this.isBusy = true;
      try {
        await ReturnFormLinkService.sendReturnFormLink(
          this.ticket.ticket_id,
          String(this.email).trim()
        );
        this.notifySuccess("Return form sent to the customer.");
        await this.loadLinks();
        if (this.$refs.emailHistory) await this.$refs.emailHistory.loadEmailQueues();
        this.$emit("sent");
      } catch (error) {
        this.notifyError("Could not send the return form.");
      } finally {
        this.isBusy = false;
      }
    },
    async revoke(link) {
      const confirmed = await this.confirmAction({
        message: "Revoke this return form link?",
        header: "Confirm Action",
      });

      if (!confirmed) {
        return;
      }

      this.isBusy = true;
      try {
        await ReturnFormLinkService.revokeReturnFormLink(link.token_id, "");
        this.notifySuccess("Return form link revoked.");
        await this.loadLinks();
      } catch (error) {
        this.notifyError("Could not revoke the link.");
      } finally {
        this.isBusy = false;
      }
    },
    formatDateTime(unixTimestamp) {
      if (!unixTimestamp) {
        return "";
      }
      return this.$moment.unix(unixTimestamp).format("DD/MM/YYYY HH:mm");
    },
  },
};
</script>
